import { FormInstance } from "antd";
import { SorterResult } from "antd/es/table/interface";
import {
  Error,
  Paging,
} from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import { ErrorCodeManagementInjection } from "features/errorCodeManagement/di/ErrorCodeManagementInjection";
import { ErrorCodeActionRequestEntity } from "features/errorCodeManagement/domain/entities/ErrorCodeActionRequestEntity";
import { ErrorCodeRequestEntity } from "features/errorCodeManagement/domain/entities/ErrorCodeRequestEntity";
import { ErrorCodeResponseEntity } from "features/errorCodeManagement/domain/entities/ErrorCodeResponseEntity";
import { ErrorCodeManagementUseCase } from "features/errorCodeManagement/domain/useCases/ErrorCodeManagementUseCase";
import { isEmpty } from "lodash";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { ActionTypeEnum } from "utils/enums/ActionTypeEnum";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";

class ErrorCodeManagementInput implements Disposable {
  public fetchList = new BehaviorSubject<ErrorCodeRequestEntity>({});
  public fetchDetail = new BehaviorSubject<ErrorCodeRequestEntity>({});
  public fetchService = new BehaviorSubject<BaseFilterListRequestModel>({});
  public loading = new BehaviorSubject<boolean>(false);
  public handleActions = new BehaviorSubject<{
    body?: ErrorCodeActionRequestEntity;
    action?: ActionTypeEnum;
  }>({});

  [Symbol.dispose](): void {
    this.fetchList.complete();
    this.fetchService.complete();
    this.fetchDetail.complete();
    this.loading.complete();
    this.handleActions.complete();
  }
}

class ErrorCodeManagementOutput implements Disposable {
  public savedList = new BehaviorSubject<Array<ErrorCodeResponseEntity>>([]);
  public savedService = new BehaviorSubject<Array<BaseFilterListResponseModel>>(
    [],
  );
  public savedPaging = new BehaviorSubject<Paging>({});
  public savedDetail = new BehaviorSubject<ErrorCodeResponseEntity>({});
  public isActionSuccess = new BehaviorSubject<boolean>(false);
  public isError = new BehaviorSubject<Error>({});

  [Symbol.dispose](): void {
    this.savedList.complete();
    this.savedDetail.complete();
    this.savedPaging.complete();
    this.savedService.complete();
    this.isActionSuccess.complete();
    this.isError.complete();
  }
}

@injectable()
export class ErrorCodeManagementViewModel extends BaseViewModel<
  ErrorCodeManagementInput,
  ErrorCodeManagementOutput
> {
  private useCase: ErrorCodeManagementUseCase;

  constructor() {
    super(new ErrorCodeManagementInput(), new ErrorCodeManagementOutput());
    this.useCase = ErrorCodeManagementInjection.getUseCase();
  }

  public buildTableQuery(
    pagination: any,
    sorter: SorterResult,
    filterForm: React.RefObject<FormInstance<any>>,
    prevParams: ErrorCodeRequestEntity,
  ) {
    const formValues = filterForm?.current?.getFieldsValue(true) || {};
    const { created, ...rest } = formValues;

    const order =
      sorter?.order === "descend"
        ? `${String(sorter?.column?.dataIndex)} DESC`
        : sorter?.order === "ascend"
          ? `${String(sorter?.column?.dataIndex)} ASC`
          : `createdAt DESC`;

    const query = {
      ...prevParams,
      ...rest,
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
      order,
    };

    const paginationState = {
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
    };

    return {
      query,
      pagination: paginationState,
    };
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.input.loading.next(true);
        const cache = productCache?.getCache(CacheKeyEnum.modalErrorCode);
        if (
          !isEmpty(cache) &&
          params?.actionType === ActionTypeEnum.modalErrorManagement
        ) {
          this.output.savedList.next(cache["list"]);
          this.output.savedPaging.next({
            page: 0,
            limit: params?.limit || 10,
            total: cache["total"],
          });
        } else {
          const { actionType, ...restParams } = params;
          const res = await this.useCase.getList({ ...restParams });
          if (res?.data) {
            actionType === ActionTypeEnum.modalErrorManagement &&
              productCache?.saveCache({
                key: CacheKeyEnum.modalErrorCode,
                product: {
                  list: res?.data,
                  total: res?.pagination?.total,
                },
                needExpired: true,
              });
            this.output.savedList.next(res?.data);
            this.output.savedPaging.next(res?.pagination);
          }
        }
      } catch (error) {
        console.error(error);
        productCache?.resetCache(CacheKeyEnum.modalErrorCode);
        this.output.savedList.next([]);
        this.output.savedPaging.next({});
        this.output.isError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        const res = await this.useCase.getDetail(params);
        if (res?.data) {
          this.output.savedDetail.next(res?.data);
        }
      } catch (error) {
        console.error(error);
        this.output.isError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchService, async (params) => {
      try {
        const res = await this.useCase.getServiceList(params);
        if (res) {
          this.output.savedService.next(res);
        }
      } catch (error) {
        console.error(error);
      }
    });

    this.skipFirstInitial(this.input.handleActions, async (props) => {
      try {
        let res = undefined;
        const { action, body } = props;
        switch (action) {
          case ActionTypeEnum.create:
            res = await this.useCase.createErrorCode(body);
            if (res?.data) {
              this.output.isActionSuccess.next(true);
            }
            break;
          case ActionTypeEnum.update:
            res = await this.useCase.updateErrorCode(body);
            if (res?.data) {
              this.output.isActionSuccess.next(true);
            }
            break;
          case ActionTypeEnum.delete:
            res = await this.useCase.deleteErrorCode({
              code: body?.code,
            });
            if (res?.data) {
              this.output.isActionSuccess.next(true);
            }
            break;
        }
      } catch (error) {
        console.error(error);
        this.output.isError.next(error);
      }
    });
  }
}
